import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { Box, Button, Flex, Select, theme } from '@xometry/xometry_loft';
import Table from '../../../../components/Table';
import { AuthContext } from '@xometry/auth-context';
import { OrganizationUser } from '../../../../schema/generated/schema';
import OrganizationMemberHelper, {
  OrganizationActiveUser,
  PAGINATION_PAGE_SIZE,
} from './helper';

const BaseTable = ({
  members,
  MemberColumns,
  isStaff,
  hasWriteOrgPermission,
  quickActions,
  currentEmail,
  handleQuickAction,
  handleLeaveTeam,
  isReadOnly,
}: {
  members: OrganizationUser[];
  MemberColumns: any;
  isStaff: boolean;
  hasWriteOrgPermission: boolean;
  handleQuickAction: (action: string, rows: string[]) => void;
  quickActions: string[];
  currentEmail: string;
  handleLeaveTeam: () => void;
  isReadOnly: boolean;
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [maxPages, setMaxPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [currentMembers, setCurrentMembers] = useState([]);

  const { user } = useContext(AuthContext);
  const tableRef = useRef<{ resetSelection: () => void }>({
    resetSelection: () => setSelectedAction(''),
  });

  useEffect(() => {
    if (members.length > 0) {
      setMaxPages(Math.ceil(members.length / PAGINATION_PAGE_SIZE));
      setCurrentMembers(members.slice(selectedPage - 1, PAGINATION_PAGE_SIZE));
    }
  }, [members.length]);

  const currentAdminRowId = OrganizationMemberHelper.getCurrentUserAdminRowId(
    members,
    user?.emailAddress
  );
  const rowSelected = (rowIds: Record<number, boolean>) => {
    const rows = Object.keys(rowIds);
    const filteredRows = rows.filter(
      (value) => value !== currentAdminRowId.toString()
    );
    setSelectedRows(filteredRows);
  };
  const handleQuickActionSelect = (event) => {
    setSelectedAction(event.target.value);
    if (window.analytics && user) {
      window.analytics.track('Team member management quick action selected', {
        isStaff: isStaff,
        quickAction: selectedAction,
        userId: user.id,
        organizationId: members[0].organizationId,
        email: user.emailAddress,
      });
    }
  };
  const handleQuickActionUpdate = () => {
    if (!['Quick Actions', ''].includes(selectedAction)) {
      handleQuickAction(selectedAction, selectedRows);
      tableRef?.current?.resetSelection();
    }
  };
  const handleQuickActionCancel = () => {
    tableRef?.current?.resetSelection();
  };

  const handleTeamMemberLeave = () => {
    handleLeaveTeam();
  };

  // Disables selection on row when the conditions are true
  const disabledRowCheck = (entry: OrganizationActiveUser): boolean => {
    return (
      entry.userData.role === 'Admin' &&
      entry.userData.email === user?.emailAddress
    );
  };

  // React callback to manage fetch calls
  const fetchMemberData = useCallback(
    (currentPage = 1, pageSize = 5) => {
      OrganizationMemberHelper.fetchMembersInMemory(
        members,
        setCurrentMembers,
        currentPage,
        pageSize
      );
    },
    [selectedPage, members]
  );

  const hasWriteAccess = isStaff || hasWriteOrgPermission;
  return (
    <>
      <Flex>
        <Flex
          alignItems="center"
          borderRadius="4px"
          padding="4px"
          color={theme.colors.darkBlues[90]}
          margin="12px 20px 0"
          height={'fit-content'}
          width="100%"
        >
          {hasWriteAccess && selectedRows.length === 0 && !isReadOnly && (
            <Flex
              backgroundColor={theme.colors.blues[10]}
              padding="8px 8px"
              borderRadius="3px"
            >
              <Box fontSize="20px" marginRight="8px">
                <i className="fas fa-bolt" />
              </Box>
              <Box fontSize="13px" margin={['6px 0px', '0px']}>
                Select one or more team members to assign a role or remove
                access to the team.
              </Box>
            </Flex>
          )}
          {hasWriteAccess && selectedRows.length > 0 && (
            <Flex
              justifyContent="space-between"
              alignItems="baseline"
              width="100%"
            >
              <Box fontSize="16px" marginRight="8px">
                {selectedRows.length} Selected
              </Box>
              <Flex>
                <Button
                  kind="text"
                  scale={theme.scale.small}
                  onClick={handleQuickActionCancel}
                  marginRight="8px"
                >
                  Cancel
                </Button>
                <Button
                  kind="text-blue"
                  scale={theme.scale.small}
                  onClick={handleQuickActionUpdate}
                  marginRight="8px"
                >
                  Update
                </Button>
                <Select onChange={handleQuickActionSelect}>
                  <option key="placeholder">Quick Actions</option>
                  {quickActions.map((action) => (
                    <option key={action}>{action}</option>
                  ))}
                </Select>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Box
        color={theme.colors.darkBlues[100]}
        padding={['0 24px 24px', '0 20px 20px']}
      >
        <Table
          ref={tableRef}
          data={currentMembers}
          columns={MemberColumns(handleTeamMemberLeave, currentEmail)}
          hideHeader={false}
          clickableRow={hasWriteAccess}
          selectRows={rowSelected}
          handleLeaveTeam={handleTeamMemberLeave}
          fetchCallback={fetchMemberData}
          disabledCondition={disabledRowCheck}
          showSelectColumn={true}
          recordPageCount={maxPages}
          pageSize={PAGINATION_PAGE_SIZE}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          isReadOnly={!hasWriteAccess || isReadOnly}
        />
      </Box>
    </>
  );
};

BaseTable.defaultProps = {
  members: null,
  MemberColumns: null,
  hasWriteOrgPermission: false,
  isStaff: false,
};

export default BaseTable;
